
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'

import NewButton from '@/ui-kit/NewButton/index.vue'
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import NewModal from '@/common/new-components/NewModal/index.vue'
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

import { UserT } from '@/core/types/users.types'
import { users } from '@/core/realisations/users.realisation'

import { DataState } from '@/core/classes/DataState.class'
import { GetWorkplaceItemsParams } from '@/new-services/types/reports'

import tmsAPI from '@/new-services/tms.service'
import locationAPI from '@/new-services/location.service'

import filtersIcon from '@/assets/icons/filters.svg'
import { CityT } from '@/core/types/cities.types'
import useNotifications from '@/common/composable/useNotifications'
import { required, requiredIf } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'
import { CalculateParams, CalculateResponse } from '@/new-services/types/tms'
import { useFile } from '@/common/composable/useFile'
import { useRouter } from 'vue-router'

const TYPE_OPTIONS = [
    { id: 1, name: 'LTL', value: 'ltl' },
    { id: 2, name: 'FTL', value: 'ftl' },
]

export default defineComponent({
    components: {
        TheSelect,
        NewButton,
        PageTitle,
        filtersIcon,
        Checkbox,
        NewModal,
        NewSelect,
        NewCheckbox,
        Cheap,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const router = useRouter()

        const state = reactive({
            is_open: false,
        })

        const calculator_state = reactive({
            is_open: false,
            is_loading: false,
            //
            fromCityId: null as number | null,
            toCityId: null as number | null,
            transitCityIds: [] as Array<number | null>,
            //
            response: null as CalculateResponse | null,
            isTransit: false,
        })

        const export_state = reactive({
            is_open: false,
            is_loading: false,
            cityName: null as string | null,
        })

        const validation$ = useVuelidate(
            {
                fromCityId: { required },
                toCityId: { required },
                transitCityIds: {
                    $each: {
                        required,
                    },
                },
            },
            calculator_state
        )

        const filters = reactive({
            type: [] as string[],
            logistIds: [] as number[],
            isDelayed: null as number | null,
            isProject: null as number | null,
        })

        const data_state = reactive({
            logists: new DataState<UserT>({}),
            cities: new DataState<CityT>({}),
        })

        const loadLogists = async (page: number, name?: string) => {
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, name, 0, 1)

                data_state.logists.list = logists.data

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const loadMoreLogists = async (page: number) => {
            if (page > data_state.logists.pagination.total_pages) return
            data_state.logists.pagination.current_page = page

            try {
                data_state.logists.is_loading = true

                const logists = await users.getLogists(page, undefined, 0)
                data_state.logists.list = data_state.logists.list.concat(logists.data)

                data_state.logists.pagination.total_pages = logists.meta.last_page

                data_state.logists.is_failed = false
            } catch (error) {
                data_state.logists.is_failed = true
                console.error(error)
            } finally {
                data_state.logists.is_loading = false
            }
        }

        const searchLogists = async (search_query: string) => {
            loadLogists(1, search_query)
        }

        const unshiftFindedLogist = async (logist: UserT) => {
            await loadLogists(data_state.logists.pagination.current_page)
            data_state.logists.list.unshift(logist)
        }

        const onSubmit = () => {
            if (props.loading) return

            const params: GetWorkplaceItemsParams = {
                ...filters,
            }

            emit('submit', params)
        }

        const onReset = () => {
            filters.type = []
            filters.logistIds = []
            filters.isDelayed = null
            filters.isProject = null

            emit('reset')
        }

        const toggle = () => (state.is_open = !state.is_open)

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка городов произошла ошибка',
                })
            } finally {
                data_state.cities.is_loading = false
            }
        }

        const resetCalculator = () => {
            calculator_state.isTransit = false
            calculator_state.fromCityId = null
            calculator_state.toCityId = null
            calculator_state.transitCityIds = []
            calculator_state.response = null
        }

        const toggleCalculator = () => {
            calculator_state.is_open = !calculator_state.is_open
            if (calculator_state.is_open) {
                loadCities()
            } else {
                resetCalculator()
            }
        }

        const resetExport = () => {
            calculator_state.fromCityId = null
            calculator_state.toCityId = null
        }

        const toggleExport = () => {
            export_state.is_open = !export_state.is_open
            if (export_state.is_open) {
                loadCities()
            } else {
                resetExport()
            }
        }

        const doChecks = () => {
            validation$.value.$touch()
            if (
                validation$.value.$invalid ||
                (calculator_state.isTransit &&
                    !calculator_state.transitCityIds.every((city) => city))
            ) {
                return false
            }

            if (calculator_state.fromCityId === calculator_state.toCityId) {
                alert('Город отправления и получения не могут совпадать')
                return false
            }
            const uniqueTransitCityIds = new Set(calculator_state.transitCityIds)
            if (uniqueTransitCityIds.size !== calculator_state.transitCityIds.length) {
                alert('Транзитные города не должны повторяться')
                return false
            }
            if (
                calculator_state.transitCityIds.includes(calculator_state.fromCityId) ||
                calculator_state.transitCityIds.includes(calculator_state.toCityId)
            ) {
                alert('Транзитный город не может совпадать с городом отправления или получения')
                return false
            }

            return true
        }

        const calculate = async () => {
            if (!doChecks()) return

            try {
                calculator_state.is_loading = true

                const payload: CalculateParams = {
                    fromCityId: calculator_state.fromCityId as number,
                    toCityId: calculator_state.toCityId as number,
                }
                if (calculator_state.transitCityIds.length) {
                    payload.transitCityIds = [...calculator_state.transitCityIds] as number[]
                }

                const response = await tmsAPI.calculate(payload)
                calculator_state.response = response.data.data
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Произошла ошибка`,
                })
            } finally {
                calculator_state.is_loading = false
            }
        }

        const { saveFile } = useFile()

        const exportByCity = async () => {
            if (!export_state.cityName) return

            try {
                export_state.is_loading = true

                const response = await tmsAPI.exportByCity(export_state.cityName)
                saveFile(response.data, `Экспорт`)

                notifications.notify({ title: 'Экспорт успешно завершен!', type: 'success' })
                toggleExport()
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: `Произошла ошибка`,
                })
            } finally {
                calculator_state.is_loading = false
            }
        }

        const deleteTransitCity = (transit_city_index: number) => {
            calculator_state.transitCityIds.splice(transit_city_index, 1)
        }

        const addTransitCity = () => calculator_state.transitCityIds.push(null)

        const onCheckboxChange = () => {
            if (!calculator_state.isTransit) {
                calculator_state.transitCityIds = []
            }
        }

        const getCityName = (id: number | null) => {
            if (!id) return '...'

            const cityFound = data_state.cities.list.find((item) => item.id === id)

            if (cityFound) {
                return cityFound.name
            }

            return '...'
        }

        const round = (value: number, digits = 2) => Number(value.toFixed(digits))

        const deleteDirection = (index: number) => {
            if (!calculator_state.response || !calculator_state.response.directions) return
            const [removedDirection] = calculator_state.response.directions.splice(index, 1)

            calculator_state.response.totalInvoices -= removedDirection.invoiceCount
            calculator_state.response.totalWeight = round(
                calculator_state.response.totalWeight - removedDirection.totalWeight
            )
            calculator_state.response.totalVolume = round(
                calculator_state.response.totalVolume - removedDirection.totalVolume
            )
        }

        const getCalcConfirmBtnText = computed(() => {
            return calculator_state.response ? 'Создать заявку' : 'Рассчитать остатки'
        })

        const manageCalc = () => {
            if (calculator_state.response) {
                const query: Record<string, string> = {
                    from: String(calculator_state.fromCityId),
                    to: String(calculator_state.toCityId),
                    weight: String(calculator_state.response.totalWeight),
                    volume: String(calculator_state.response.totalVolume),
                }

                if (calculator_state.isTransit && calculator_state.transitCityIds.length) {
                    query.tr = calculator_state.transitCityIds
                        .filter((id): id is number => id !== null)
                        .join(',')
                }

                router.push({
                    path: '/tms/trips/create-bid',
                    query,
                })
                return
            }

            calculate()
        }

        const isCalcDisabled = computed(() => {
            return (
                calculator_state.response &&
                (!calculator_state.response.totalWeight || !calculator_state.response.totalVolume)
            )
        })

        onBeforeMount(async () => {
            loadLogists(1)
            onSubmit()
        })

        return {
            filters,
            state,
            calculator_state,
            export_state,
            data_state,
            onSubmit,
            onReset,
            TYPE_OPTIONS,
            loadLogists,
            loadMoreLogists,
            searchLogists,
            unshiftFindedLogist,
            toggle,
            toggleCalculator,
            loadCities,
            validation$,
            calculate,
            exportByCity,
            deleteTransitCity,
            addTransitCity,
            onCheckboxChange,
            resetCalculator,
            getCityName,
            deleteDirection,
            toggleExport,
            getCalcConfirmBtnText,
            isCalcDisabled,
            manageCalc,
        }
    },
})
